import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/newComponents/form/Form";
import Heading from "@/newComponents/typography/Heading";
import { useUIStateStore } from "@/store/uiStateStore";

import type { WriterFormData } from "../../../writers/hooks/useWriterForm";

import { useGetSocieties } from "../../../../../../api/queries";
import useSiteConfigAuth from "../../../../../../api/sanity/queries/getSiteConfigWithCode";
import { Button } from "../../../../../../newComponents/button/Button";
import { DialogFooter } from "../../../../../../newComponents/dialog/Dialog";
import { FormInput } from "../../../../../../newComponents/input/FormInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../../newComponents/select/Select";
import Text from "../../../../../../newComponents/typography/Text";
import { useWriterForm } from "../../../writers/hooks/useWriterForm";
import { useWriterMutation } from "../../../writers/hooks/useWriterMutation";
import useWriterStore from "../store/writerStore";

type CreateWriterModalProps = {
  onClose: () => void;
  editingWriterCode?: string | null;
};

export default function CreateWriterModal({ onClose, editingWriterCode }: CreateWriterModalProps) {
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { selectedWriters, availableWriters } = useWriterStore();
  const societiesQuery = useGetSocieties();
  const { data: siteConfigData } = useSiteConfigAuth();

  const { t } = useTranslation("album");
  // State for API validation errors
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Use the custom hooks for writer form logic and mutations
  const { form, isEditing, editingWriter, editingWriterFromAvailable, writerQuery, isUSPro } = useWriterForm({
    editingWriterCode,
    availableWriters,
    selectedWriters,
  });

  const { createWriter, updateWriter, pending } = useWriterMutation({
    onSuccess: onClose,
    onValidationError: (messages) => {
      // Display API validation errors in the form instead of toasts
      setValidationErrors(messages);
    },
  });

  // Form submission handler using the mutation hooks
  const onFormSubmit = async (data: WriterFormData) => {
    // Clear any previous validation errors
    setValidationErrors([]);

    try {
      if (isEditing && editingWriterCode != null && editingWriterCode !== "") {
        await updateWriter(
          data,
          isUSPro,
          editingWriterCode,
          editingWriter,
          editingWriterFromAvailable ?? null,
          writerQuery.data,
        );
      }
      else {
        await createWriter(data, isUSPro);
      }
    }
    catch {
      // Error handling is done in the mutation hooks
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Heading as="h3">{isEditing ? t("edit-writer") : t("create-new-writer")}</Heading>
        <Text as="p">
          {isEditing
            ? t("edit-writer-description")
            : t("create-writer-description")}
        </Text>

        {/* Display API validation errors */}
        {validationErrors.length > 0 && (
          <div className="p-4 bg-red-700 border border-red-200 rounded-md">
            <div className="flex">
              <div className="ml-3">
                <Text as="subtitle" className="text-white">
                  {validationErrors.length === 1 ? t("validaton-error") : t("validaton-errors")}
                </Text>
                <div className="mt-2 text-sm text-white">
                  {validationErrors.length === 1 ? (
                    <Text className="text-white" as="p">{validationErrors[0]}</Text>
                  ) : (
                    <ul className="space-y-1 list-disc list-inside">
                      {validationErrors.map(error => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit(onFormSubmit)(e);
            }}
            className="space-y-4"
          >

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("first-name")}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("enter-first-name") ?? "Enter first name"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("last-name")}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("enter-last-name") ?? "Enter last name"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="middlename"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("middle-name")}
                  </FormLabel>
                  <FormControl>
                    <FormInput
                      placeholder={t("enter-middle-name") ?? "Enter last name (optional)"}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="caeipinumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("cae-ipi-number")}
                  </FormLabel>
                  <FormControl>
                    <FormInput
                      placeholder={t("enter-cae-ipi-number") ?? "Enter CAE/IPI number (optional, unless PRO is selected below)"}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="societycisaccode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("performing-rights-organization")}
                  </FormLabel>
                  <FormControl>
                    <Select
                      value={(field.value != null && field.value !== "") ? field.value : "NO_PRO"}
                      onValueChange={(value) => {
                        // Convert "NO_PRO" back to empty string for form data
                        field.onChange(value === "NO_PRO" ? "" : value);
                      }}
                    >
                      <SelectTrigger className="w-full pl-0">
                        {/* <SelectValue placeholder={societiesQuery.isLoading ? "Loading PROs..." : "Select Writer PRO"} /> */}
                        <SelectValue placeholder={societiesQuery.isLoading ? t("loading-pros") : t("select-writer-pro")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NO_PRO">
                          {t("no-pro")}
                        </SelectItem>
                        {societiesQuery.data?.societies?.map(society => (
                          <SelectItem key={society.cisaccode} value={society.cisaccode}>
                            {society.name}
                            {" "}
                            (
                            {society.cisaccode}
                            )
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                  {societiesQuery.error && (
                    <Text as="small" className="text-red-600">
                      {t("failed-to-load-pros")}
                    </Text>
                  )}
                </FormItem>
              )}
            />

            {/* Date of Birth - only show if required by site config */}
            {siteConfigData?.date_of_birth_required && (
              <FormField
                control={form.control}
                name="dateofbirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("date-of-birth")}
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        type="date"
                        placeholder={t("select-date-of-birth") ?? "Select date of birth (optional)"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Associated Publisher fields - only show for US PROs */}
            {isUSPro && (
              <>
                <FormField
                  control={form.control}
                  name="associatedPublisherName"
                  render={({ field }) => (
                    <FormItem>
                      {/* <FormLabel>Associated Publisher Name</FormLabel> */}
                      <FormLabel>
                        {t("associated-publisher-name")}
                      </FormLabel>
                      <FormControl>
                        <FormInput
                          placeholder={t("enter-publisher-name") ?? "Enter publisher name (optional)"}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="associatedPublisherCAEIPI"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("associated-publisher-cae-ipi")}
                      </FormLabel>
                      <FormControl>
                        <FormInput
                          placeholder={t("enter-publisher-cae-ipi") ?? "Enter publisher CAE/IPI (optional)"}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <DialogFooter className="pt-4">
              <Button
                variant="secondary"
                // label="Cancel"
                label={t("cancel")}
                onClick={onClose}
                disabled={pending}
                type="button"
              />
              <Button
                variant={isTunecore ? "primary" : "secondary"}
                label={pending
                  // ? (isEditing ? "Updating..." : "Creating...")
                  ? (isEditing ? t("updating-writer") : t("creating-writer"))
                  // : (isEditing ? "Update Writer" : "Create Writer")}
                  : (isEditing ? t("update-writer") : t("create-writer"))}
                disabled={pending}
                type="submit"
              />
            </DialogFooter>
          </form>
        </Form>
      </div>
    </div>
  );
}
